import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';

import './meals.css';
import { Table, Button, Input, Select, Space, Tag, Modal, message, Card, Row, Col, Alert } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, CalendarOutlined, ShopOutlined } from '@ant-design/icons';
import { useMeals, useMealActions } from '../../../hooks/useMeals';
import mealService from '../../../services/meal/meal.service';
import shopService from '../../../services/shop/shop.service';
import { Meal, mealTypes } from '../../../types/meal.types';
import { all_routes } from '../../../router/all_routes';
import { handleApiError } from '../../../utils/errorHandler';

import CreateMealModal from './components/CreateMealModal';
import UpdateMealModal from './components/UpdateMealModal';
import dayjs from 'dayjs';

const { Option } = Select;
const { confirm } = Modal;


const MealsPage: React.FC = () => {

  const routes = all_routes;

  const {
    filteredMeals,
    loading,
    error,
    selectedMealType,
    searchTerm,
    fetchMeals,
    handleMealTypeChange,
    handleSearchChange,

    getWeeklyMeals
  } = useMeals();

  const { deleteMeal, assignMealToDate, removeMealFromDate } = useMealActions();

  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [selectedMeal, setSelectedMeal] = useState<Meal | null>(null);
  const [todaysMeals, setTodaysMeals] = useState<Meal[]>([]);
  
  // Şube seçimi için state'ler
  const [branches, setBranches] = useState<any[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null);

  // Haftalık yemek planı için state'ler
  const [currentWeek, setCurrentWeek] = useState<dayjs.Dayjs[]>([]);
  const [weeklyMealPlan, setWeeklyMealPlan] = useState<{ [key: string]: Meal[] }>({});
  const [draggedMeal, setDraggedMeal] = useState<Meal | null>(null);
  const [allMealsForDrag, setAllMealsForDrag] = useState<Meal[]>([]);
  const [dragFilterType, setDragFilterType] = useState<string>('all');

  const mealsForDragDisplay = dragFilterType === 'all'
    ? allMealsForDrag
    : allMealsForDrag.filter((meal) => meal.type === dragFilterType);

  // Şubeleri yükle
  const fetchBranches = async () => {
    try {
      const branchData = await shopService.getShops();
      setBranches(branchData);
      
      // İlk şubeyi otomatik seç (varsa)
      if (branchData.length > 0 && !selectedBranchId) {
        setSelectedBranchId(branchData[0].id);
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  // Tüm yemekleri yükle (drag & drop için)
  const loadAllMealsForDrag = async () => {
    if (!selectedBranchId) return;
    
    try {
      // /meals endpoint'ini kullanarak tüm yemekleri getir
      const response = await mealService.getAllMeals(selectedBranchId);
      setAllMealsForDrag(response);
    } catch (error) {
      handleApiError(error);
    }
  };

  // Haftalık yemek planını yükle (parametreli ve referans-stabil)
  const loadWeeklyMealPlan = useCallback(async (week: dayjs.Dayjs[]) => {
    if (!selectedBranchId) return;
    
    try {
      if (week.length === 0) {
        return;
      }

      if (week.length > 0) {
        const startDate = week[0].format('YYYY-MM-DD');
        const endDate = week[6].format('YYYY-MM-DD');

        const weeklyMeals = await getWeeklyMeals({
          startDate,
          endDate,
          branchId: selectedBranchId
        });

        // Yemekleri günlere göre grupla
        const groupedMeals: { [key: string]: Meal[] } = {};
        weeklyMeals.forEach(meal => {
          // Backend'den gelen gerçek format: dates array'i kullan (artık object array)
          if (meal.dates && Array.isArray(meal.dates) && meal.dates.length > 0) {
            meal.dates.forEach(dateObj => {
              // dateObj artık { date: string, userFeedback: null } formatında
              const dateStr = typeof dateObj === 'string' ? dateObj : dateObj.date;
              
              if (!groupedMeals[dateStr]) {
                groupedMeals[dateStr] = [];
              }
              // Her tarih için yemeği kopyala
              const mealCopy = { ...meal, date: dateStr };
              groupedMeals[dateStr].push(mealCopy);
            });
          }
        });

        setWeeklyMealPlan(groupedMeals);
      }
    } catch (error) {
      handleApiError(error);
    }
  }, [getWeeklyMeals, selectedBranchId]);

  // İlk yükleme - şubeleri getir
  useEffect(() => {
    fetchBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Şube seçildiğinde yemekleri yükle
  useEffect(() => {
    if (selectedBranchId) {
      fetchMeals(undefined, selectedBranchId);
      loadTodaysMeals();
      loadAllMealsForDrag(); // Tüm yemekleri yükle
      
      // Haftalık takvimi başlat
      const today = dayjs();
      const startOfWeek = today.startOf('week');
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push(startOfWeek.add(i, 'day'));
      }
      setCurrentWeek(week);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranchId]);

  // currentWeek değiştiğinde haftalık planı yükle
  useEffect(() => {
    if (currentWeek.length > 0 && selectedBranchId) {
      loadWeeklyMealPlan(currentWeek);
    }
  }, [currentWeek, selectedBranchId, loadWeeklyMealPlan]);

  // Haftalık takvim başlatma
  const initializeCurrentWeek = () => {
    const today = dayjs();

    const startOfWeek = today.startOf('week');

    const week = [];

    for (let i = 0; i < 7; i++) {
      week.push(startOfWeek.add(i, 'day'));
    }

    setCurrentWeek(week);
  };

  // Önceki haftaya git
  const goToPreviousWeek = () => {
    setCurrentWeek(prev => prev.map(day => day.subtract(1, 'week')));
  };

  // Sonraki haftaya git
  const goToNextWeek = () => {
    setCurrentWeek(prev => prev.map(day => day.add(1, 'week')));
  };

  // Drag başlatma
  const handleDragStart = (e: React.DragEvent, meal: Meal) => {
    setDraggedMeal(meal);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Drop işlemi
  const handleDrop = async (e: React.DragEvent, targetDate: string) => {
    e.preventDefault();
    if (draggedMeal && selectedBranchId) {
      try {
        // API'ye yemeği güne ata
        const result = await assignMealToDate(draggedMeal.id, {
          date: targetDate,
          branchId: selectedBranchId
        });

        if (result) {
          message.success(`${draggedMeal.title} başarıyla ${dayjs(targetDate).format('DD.MM.YYYY')} tarihine atandı`);

          // By-date endpoint'ini çağırarak spesifik günün yemeklerini güncelle
          const updatedMeals = await mealService.getMealsByDate({
            date: targetDate,
            branchId: selectedBranchId
          });

          // WeeklyMealPlan state'ini sadece bu tarih için güncelle
          setWeeklyMealPlan(prev => ({
            ...prev,
            [targetDate]: updatedMeals
          }));

          // Eğer bugüne yemek eklendiyse bugünkü yemekleri de güncelle
          const today = dayjs().format('YYYY-MM-DD');
          if (targetDate === today) {
            setTodaysMeals(updatedMeals);
          }
        }
      } catch (error) {
        handleApiError(error);
      }
      setDraggedMeal(null);
    }
  };

  // Drag over işlemi
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Yemek planından yemek kaldırma
  const removeMealFromPlan = async (date: string, mealId: number) => {
    if (!selectedBranchId) return;
    
    try {
      const success = await removeMealFromDate(mealId, date);
      if (success) {
        // By-date endpoint'ini çağırarak güncel durumu al
        const updatedMeals = await mealService.getMealsByDate({
          date,
          branchId: selectedBranchId
        });

        setWeeklyMealPlan(prev => ({
          ...prev,
          [date]: updatedMeals
        }));

        // Eğer bugünden yemek kaldırıldıysa bugünkü yemekleri de güncelle
        const today = dayjs().format('YYYY-MM-DD');
        if (date === today) {
          setTodaysMeals(updatedMeals);
        }
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  // Belirli bir günün yemeklerini getir
  const getMealsForDay = (date: string) => {
    const meals = weeklyMealPlan[date] || [];
    return meals;
  };

  const loadTodaysMeals = async () => {
    if (!selectedBranchId) return;
    
    const today = dayjs().format('YYYY-MM-DD');

    const meals = await mealService.getMealsByDate({
      date: today,
      branchId: selectedBranchId
    });

    setTodaysMeals(meals);
  };

  const handleDelete = async (mealId: number) => {
    try {
      const success = await deleteMeal(mealId);
      if (success && selectedBranchId) {
        fetchMeals(undefined, selectedBranchId); // Listeyi yenile
        loadAllMealsForDrag(); // Drag listesini de güncelle
        loadTodaysMeals(); // Bugünkü yemekleri güncelle
        loadWeeklyMealPlan(currentWeek); // Haftalık planı da güncelle
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const showDeleteConfirm = (meal: Meal) => {
    confirm({
      title: 'Yemeği silmek istediğinize emin misiniz?',
      icon: <ExclamationCircleOutlined />,
      content: `${meal.title} (${dayjs(meal.date).format('DD.MM.YYYY')}) kalıcı olarak silinecek.`,
      okText: 'Evet, Sil',
      okType: 'danger',
      cancelText: 'İptal',
      onOk() {
        handleDelete(meal.id);
      },
    });
  };



  const handleEdit = (meal: Meal) => {
    setSelectedMeal(meal);
    setUpdateModalVisible(true);
  };

  const getMealType = (type: string) => {
    const mealType = mealTypes.find(mt => mt.value === type);
    return mealType ? mealType.label : type;
  };

  const columns = [
    {
      title: 'Yemek Adı',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: Meal) => (
        <div>
          <div className="font-weight-bold">{text}</div>
          {record.description && (
            <small className="text-muted">
              {record.description.length > 20
                ? `${record.description.slice(0, 20)}...`
                : record.description}
            </small>
          )}
        </div>
      ),
    },
    {
      title: 'Yemek Türü',
      dataIndex: 'type',
      key: 'type',
      render: (text: string) => (
        <Tag color="blue" className="text-capitalize">{getMealType(text)}</Tag>
      ),
    },

    {
      title: 'İşlemler',
      key: 'actions',
      render: (record: Meal) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Düzenle
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => showDeleteConfirm(record)}
          >
            Sil
          </Button>
        </Space>
      ),
    },
  ];

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="page-header">
          <div className="row">
            <div className="col-sm-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item active">Yemek Yönetimi</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Şube Seçici */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <Card>
              <Space align="center" size="large" style={{ width: '100%', justifyContent: 'space-between' }}>
                <Space align="center">
                  <ShopOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '16px' }}>Şube Seçin</div>
                    <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
                      Yemek işlemleri için şube seçmeniz gerekmektedir
                    </div>
                  </div>
                </Space>
                <Select
                  placeholder="Şube seçin"
                  style={{ width: 300 }}
                  value={selectedBranchId}
                  onChange={setSelectedBranchId}
                  size="large"
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {branches.map((branch) => (
                    <Option key={branch.id} value={branch.id}>
                      {branch.name}
                    </Option>
                  ))}
                </Select>
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Şube seçilmediğinde uyarı */}
        {!selectedBranchId && (
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={24}>
              <Alert
                message="Şube Seçilmedi"
                description="Yemek işlemlerine devam etmek için lütfen yukarıdan bir şube seçin."
                type="warning"
                showIcon
              />
            </Col>
          </Row>
        )}

        {/* Today's Meals - Minimal modern UI */}
        {selectedBranchId && todaysMeals.length > 0 && (
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={24}>
              <Card
                bordered={false}
                className="card-modern today-meals-card"
                title={
                  <div className="today-header">
                    <CalendarOutlined />
                    <span className="today-title">Bugün</span>
                    <span className="today-date">{dayjs().format('DD MMMM')}</span>
                  </div>
                }
              >
                <div className="today-meals-grid">
                  {todaysMeals.map(meal => (
                    <div key={meal.id} className="today-meal-item">
                      <div className="today-meal-title">{meal.title}</div>
                      <div className="today-meal-meta">
                        <Tag className="meal-type-chip">{getMealType(meal.type)}</Tag>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </Col>
          </Row>
        )}

        {/* Main Content - Sadece şube seçildiyse göster */}
        {selectedBranchId && (<>
        <div className="row">
          <div className="col-sm-12">
            <div className="card card-table show-entire">
              <div className="card-body">
                {/* Toolbar */}
                <div className="page-table-header mb-2">
                  <div className="row align-items-center">
                    <div className="col">
                      <div className="doctor-table-blk">
                        <h3>Yemek Listesi</h3>
                        <div className="doctor-search-blk">
                          <div className="top-nav-search table-search-blk">
                            <Input
                              placeholder="Yemek adı ara..."
                              prefix={<SearchOutlined />}
                              value={searchTerm}
                              onChange={(e) => handleSearchChange(e.target.value)}
                              style={{ width: 200 }}
                            />
                          </div>
                          <div className="add-group d-flex justify-content-end">

                            <Space>
                              <Select
                                placeholder="Yemek Türü"
                                style={{ width: 120 }}
                                value={selectedMealType}
                                onChange={handleMealTypeChange}
                              >
                                <Option value="all">Tümü</Option>
                                {mealTypes.map(type => (
                                  <Option key={type.value} value={type.value}>{type.label}</Option>
                                ))}
                              </Select>

                              <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setCreateModalVisible(true)}
                                disabled={!selectedBranchId}
                              >
                                Yeni Yemek Ekle
                              </Button>
                            </Space>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table */}
                <div className="table-responsive">
                  <Table
                    columns={columns}
                    dataSource={filteredMeals}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                      total: filteredMeals.length,
                      pageSize: 10,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} items`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Haftalık Yemek Planı */}
        <div className="row">
          <div className="col-sm-12">
            <div className="card card-table show-entire">
              <div className="card-body">


                {/* Üst kısım - Mevcut Yemekler (Modern Horizontal Layout) */}
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="card shadow-sm border-0">
                      <div className="card-header bg-gradient-primary text-white d-flex justify-content-between align-items-center">
                        <div>
                          <h5 className="mb-0 text-white">
                            <i className="fas fa-utensils me-2"></i>
                            Mevcut Yemekler
                          </h5>
                          <small className="text-black-50">Sürükleyip takvime bırakın</small>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <Select
                            size="small"
                            value={dragFilterType}
                            onChange={setDragFilterType}
                            style={{ width: 140 }}
                          >
                            <Option value="all">Tümü</Option>
                            {mealTypes.map((type) => (
                              <Option key={type.value} value={type.value}>
                                {type.label}
                              </Option>
                            ))}
                          </Select>
                          <div className="badge bg-light text-dark px-3 py-2">
                            {mealsForDragDisplay.length} Yemek
                          </div>
                        </div>
                      </div>
                      <div className="card-body p-3">
                        {allMealsForDrag.length === 0 ? (
                          <div className="text-center p-5">
                            <div className="mb-3">
                              <i className="fas fa-plus-circle fa-3x text-muted"></i>
                            </div>
                            <h6 className="text-muted mb-2">Henüz yemek eklenmemiş</h6>
                            <small className="text-muted">Yeni yemek eklemek için "Yeni Yemek Ekle" butonunu kullanın</small>
                          </div>
                        ) : (
                          <div
                            className="d-flex flex-wrap gap-3 meal-list-container"
                            style={{
                              maxHeight: '200px',
                              overflowY: 'auto',
                              overflowX: 'hidden'
                            }}
                          >
                            {mealsForDragDisplay.map((meal) => (
                              <div
                                key={meal.id}
                                className="meal-card"
                                draggable
                                onDragStart={(e) => handleDragStart(e, meal)}
                                style={{
                                  cursor: 'grab',
                                  minWidth: '200px',
                                  maxWidth: '250px',
                                  flex: '0 0 auto'
                                }}
                              >
                                <div className="card h-100 border-0 shadow-sm meal-hover-effect">
                                  <div className="card-body p-3">
                                    <div className="d-flex align-items-start justify-content-between mb-2">
                                      <div className="flex-grow-1">
                                        <h6 className="card-title mb-1 fw-bold text-dark">
                                          {meal.title}
                                        </h6>
                                      </div>
                                      <i className="fas fa-grip-vertical text-muted ms-2"></i>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between">
                                      <Tag
                                        color="blue"
                                        className="text-capitalize border-0"
                                        style={{
                                          backgroundColor: '#e6f7ff',
                                          color: '#1890ff',
                                          borderRadius: '20px',
                                          padding: '4px 12px',
                                          fontSize: '11px',
                                          fontWeight: '500'
                                        }}
                                      >
                                        {getMealType(meal.type)}
                                      </Tag>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Alt kısım - Haftalık Takvim (%100 genişlik) */}
                <div className="row">
                  <div className="col-12">
                    <div className="card shadow-sm border-0">
                      <div className="card-header bg-white border-bottom">
                        <div className="d-flex justify-content-between align-items-center">
                          <h5 className="mb-0 text-dark">
                            <i className="fas fa-calendar-alt me-2 text-primary"></i>
                            Haftalık Yemek Planı
                          </h5>
                          <div className="d-flex gap-2">
                            <Button
                              onClick={goToPreviousWeek}
                              className="d-flex align-items-center btn-modern"
                            >
                              <i className="fas fa-chevron-left me-1"></i>
                              Önceki Hafta
                            </Button>
                            <Button
                              onClick={goToNextWeek}
                              className="d-flex align-items-center btn-modern"
                            >
                              Sonraki Hafta
                              <i className="fas fa-chevron-right ms-1"></i>
                            </Button>
                          </div>
                        </div>
                      </div>
                      <div className="card-body p-0">
                        <div className="table-responsive">
                          <table className="table table-hover mb-0 modern-calendar-table">
                            <thead>
                              <tr className="border-0">
                                {currentWeek.map((day, index) => (
                                  <th
                                    key={index}
                                    className={`text-center py-3 border-0 ${day.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD') ? 'today-highlight' : ''}`}
                                    style={{
                                      backgroundColor: day.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD')
                                        ? '#e6f7ff'
                                        : day.day() === 0 || day.day() === 6 ? '#f8f9fa' : '#fff',
                                      borderRadius: '8px 8px 0 0',
                                      width: '14.28%'
                                    }}
                                  >
                                    <div className="fw-bold text-dark mb-1">{day.format('dddd')}</div>
                                    <div className="small text-muted">{day.format('DD MMMM')}</div>
                                    {day.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD') && (
                                      <div className="small">
                                        <span className="badge bg-primary rounded-pill px-2 py-1">Bugün</span>
                                      </div>
                                    )}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              <tr>

                                {currentWeek.map((day, dayIndex) => {
                                  const dayStr = day.format('YYYY-MM-DD');
                                  const dayMeals = getMealsForDay(dayStr);

                                  return (
                                    <td
                                      key={dayIndex}
                                      className="position-relative day-cell"
                                      style={{
                                        minHeight: '300px',
                                        backgroundColor: dayMeals.length > 0 ? '#f8f9ff' : '#fafafa',
                                        border: '1px solid #e8e8e8',
                                        verticalAlign: 'top',
                                        padding: '12px',
                                        transition: 'background-color 0.2s ease'
                                      }}
                                      onDrop={(e) => handleDrop(e, dayStr)}
                                      onDragOver={handleDragOver}
                                    >
                                      {dayMeals.length > 0 ? (
                                        <div className="meal-list">
                                          {dayMeals.map((meal, mealIndex) => (
                                            <div
                                              key={mealIndex}
                                              className="meal-item-in-calendar mb-3"
                                              style={{
                                                backgroundColor: '#fff',
                                                border: '1px solid #d9d9d9',
                                                borderRadius: '8px',
                                                padding: '12px',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                transition: 'all 0.2s ease'
                                              }}
                                            >
                                              <div className="d-flex align-items-start justify-content-between">
                                                <div className="flex-grow-1">
                                                  <h6 className="mb-1 fw-bold text-dark">{meal.title}</h6>
                                                  <Tag
                                                    color="blue"
                                                    className="text-capitalize"
                                                    style={{
                                                      borderRadius: '12px',
                                                      fontSize: '10px',
                                                      padding: '2px 8px',
                                                      backgroundColor: '#e6f7ff',
                                                      color: '#1890ff',
                                                      border: 'none'
                                                    }}
                                                  >
                                                    {getMealType(meal.type)}
                                                  </Tag>
                                                </div>
                                                <Button
                                                  type="text"
                                                  size="small"
                                                  danger
                                                  onClick={() => removeMealFromPlan(dayStr, meal.id)}
                                                  style={{
                                                    borderRadius: '50%',
                                                    width: '24px',
                                                    height: '24px',
                                                    padding: '0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '12px'
                                                  }}
                                                >
                                                  ×
                                                </Button>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <div className="drop-zone d-flex align-items-center justify-content-center h-100">
                                          <div className="text-center p-4" style={{ opacity: 0.6 }}>
                                            <div className="mb-3">
                                              <i className="fas fa-hand-paper fa-2x text-muted"></i>
                                            </div>
                                            <div className="text-muted">
                                              <small className="fw-medium d-block">Yemek Sürükleyin</small>
                                              <small className="text-muted">Buraya bırakın</small>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modals */}
            <CreateMealModal
              visible={createModalVisible}
              branchId={selectedBranchId || undefined}
              onCancel={() => setCreateModalVisible(false)}
              onSuccess={() => {
                setCreateModalVisible(false);
                if (selectedBranchId) {
                  fetchMeals(undefined, selectedBranchId);
                  loadTodaysMeals();
                  loadAllMealsForDrag(); // Yeni yemek eklendikten sonra listeyi güncelle
                  loadWeeklyMealPlan(currentWeek); // Haftalık planı da güncelle
                }
              }}
            />

            <UpdateMealModal
              visible={updateModalVisible}
              meal={selectedMeal}
              onCancel={() => {
                setUpdateModalVisible(false);
                setSelectedMeal(null);
              }}
              onSuccess={() => {
                setUpdateModalVisible(false);
                setSelectedMeal(null);
                if (selectedBranchId) {
                  fetchMeals(undefined, selectedBranchId);
                  loadTodaysMeals();
                  loadAllMealsForDrag(); // Yemek güncellendikten sonra listeyi güncelle
                  loadWeeklyMealPlan(currentWeek); // Haftalık planı da güncelle
                }
              }}
            />
          </div>
        </div>
        </>)}
      </div>
    </div>
  );
};

export default MealsPage;